import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Frequently bought together" upsell.
 *
 * Adds the main product plus every checked companion in a single /cart/add call,
 * mirroring the multi-line add in product-form.js so the drawer, cart icon and
 * cart sections all refresh through the theme's own event pipeline.
 */
/*
 * The theme declares `const Theme = {...}` in a classic script. A top-level const
 * is a LEXICAL global, not a property of window - so bare `Theme` resolves while
 * `window.Theme` is undefined. Read it by identifier, with route fallbacks.
 */
function themeGlobal() {
  return typeof Theme !== 'undefined' ? Theme : undefined;
}

function cartRoutes() {
  const t = themeGlobal();
  return {
    add: t?.routes?.cart_add_url ?? '/cart/add.js',
    cart: t?.routes?.cart_url ?? '/cart',
  };
}

export class BundleUpsellComponent extends Component {
  requiredRefs = ['form'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.#onChange);
    this.#syncTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== 'checkbox') return;
    this.#syncTotal();
  };

  /** @returns {HTMLInputElement[]} every checkbox the shopper has left ticked */
  get #checked() {
    return Array.from(this.querySelectorAll('input[type="checkbox"][data-variant-id]')).filter((input) => input.checked);
  }

  /**
   * Recomputes the footer total from the checked rows.
   *
   * Each row carries both its list price and its bundled price, so the saving is
   * derived here rather than trusted from a setting that could drift out of sync.
   */
  #syncTotal() {
    const rows = this.#checked;
    let full = 0;
    let bundled = 0;

    for (const input of rows) {
      full += Number(input.dataset.price || 0);
      bundled += Number(input.dataset.bundlePrice || input.dataset.price || 0);
    }

    const { total, saving, count, submit } = this.refs;

    if (total) total.textContent = this.#money(bundled);
    if (count) count.textContent = String(rows.length);

    if (saving) {
      const diff = full - bundled;
      saving.textContent = diff > 0 ? this.#money(diff) : '';
      saving.hidden = diff <= 0;
    }

    // Nothing ticked means nothing to add - keep the button out of a no-op state.
    if (submit) submit.disabled = rows.length === 0;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = themeGlobal()?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  handleSubmit = (event) => {
    event.preventDefault();

    const items = this.#checked.map((input) => ({
      id: Number(input.dataset.variantId),
      quantity: 1,
    }));

    if (!items.length) return;

    const { submit } = this.refs;
    if (submit) submit.setAttribute('aria-busy', 'true');

    // Cart sections currently on the page, so Shopify re-renders them in the same round trip.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    fetch(cartRoutes().add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          return;
        }

        const cart = await fetch(`${cartRoutes().cart}.json`, {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: false,
            items: cart.items,
            source: 'bundle-upsell-component',
            sourceId: this.id?.toString?.() ?? 'bundle-upsell',
            itemCount: items.length,
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        console.warn('[bundle-upsell] add failed:', error);
      })
      .finally(() => {
        if (submit) submit.removeAttribute('aria-busy');
      });
  };
}

if (!customElements.get('bundle-upsell-component')) {
  customElements.define('bundle-upsell-component', BundleUpsellComponent);
}
